import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { requestMainData } from "../api/api-main";
import { SecurityViewTypes, TimeframeTypes, TraderTypes, ViewTypes } from "../datatypes/main";

export interface MainDataState {
    
    status: 'idle' | 'loading' | 'failed', 
    indicators: Array<any>, 
    indicatorsLastUpdated: number,
    timeframe: TimeframeTypes, 
    view: ViewTypes, 
    securityView: SecurityViewTypes,
    traderType: TraderTypes,
    previousValues: any,
    symbol: string,
    filters: Array<string>,
    filtersLastUpdated: number,
    sortCriteria: Array<string>, 
    sortDirections: Array<'ascending' | 'descending'>,
    sortLastUpdated: number,
    startingIndicators: Array<any>
}

const initialState: MainDataState = {
   
    status: 'idle', 
    indicators: new Array<any>(),
    indicatorsLastUpdated: (new Date()).getTime(), 
    timeframe: 'two_hour', 
    view: "traders-list", 
    securityView: 'all',
    traderType: "short_term", 
    previousValues: {view: "traders-list", traderType: "short_term", timeframe: "two_hour"},
    symbol: "", 
    filters: new Array<string>(),
    filtersLastUpdated: (new Date()).getTime(), 
    sortCriteria: new Array<string>(), 
    sortDirections: new Array<'ascending' | 'descending'>(), 
    sortLastUpdated: (new Date()).getTime(),
    startingIndicators: new Array<any>(),
}

export const mainRequestThunk = createAsyncThunk(
    'main/dataRequest', 
    async (userLevel: string) => {
        const response = await requestMainData(userLevel); 
        return response?.data;
    }
)



export const mainDataReducer = createSlice({
    name: 'main',
    initialState, 
    reducers: {
        updateTimeframe: (state: MainDataState, action: PayloadAction<TimeframeTypes>) =>  {
            state.timeframe = action.payload;
        }, 
        updateTraderType: (state: MainDataState, action: PayloadAction<TraderTypes>) => {
            state.traderType = action.payload;
        },
        updateView: (state: MainDataState, action: PayloadAction<ViewTypes>) => {
            state.view = action.payload;
        }, 
        updateSecurityView: (state: MainDataState, action: PayloadAction<SecurityViewTypes>) => {
            state.securityView = action.payload;
            const nextFilterset = state.filters.filter((filter) => !filter.startsWith('security-type:'));
            if (action.payload !== 'all') {
                nextFilterset.push(`security-type:${action.payload}`);
            }
            state.filters = nextFilterset;
        },
        updateSymbol: (state: MainDataState, action: PayloadAction<string>) => {
            state.symbol = action.payload;
        },
        updateFilters: (state: MainDataState, action: PayloadAction<Array<string>>) => {
            state.filters = action.payload;
            state.filtersLastUpdated = (new Date()).getTime();
        }, 
        updateSortCriteria: (state: MainDataState, action: PayloadAction<Array<string>>) => {
            state.sortCriteria = action.payload; 
            state.sortLastUpdated = (new Date()).getTime();
        },
        updateSortDirections: (state: MainDataState, action: PayloadAction<Array<'ascending' | 'descending'>>) => {
            state.sortDirections = action.payload;
            state.sortLastUpdated = (new Date()).getTime();
        }, 
        savePrevValues: (state: MainDataState) => {
            state.previousValues.view = state.view; 
            state.previousValues.traderType = state.traderType; 
            state.previousValues.timeframe = state.timeframe;
        },
        setValuesBack: (state: MainDataState) => {
            state.view = state.previousValues.view; 
            state.traderType = state.previousValues.traderType;
            state.timeframe = state.previousValues.timeframe;
        }
    }, 
    extraReducers: (builder) => {
        builder
        .addCase(mainRequestThunk.pending, (state, action) => {
            state.status = 'loading';
        })
        .addCase(mainRequestThunk.fulfilled, (state, action) => {
            if ('data' in action.payload) {
                state.indicators = action.payload.data;
                state.startingIndicators = action.payload.data;                
                state.indicatorsLastUpdated = (new Date()).getTime();
            }
            state.status = 'idle';
        })
        .addCase(mainRequestThunk.rejected, (state) => {
            state.status = 'failed';
        })
    }
})

export const { 
    updateTimeframe, updateView, updateSecurityView, updateSymbol, updateTraderType,
    updateFilters, updateSortCriteria, updateSortDirections, savePrevValues, setValuesBack
} = mainDataReducer.actions;

export const selectStatus = (state: RootState) => state.mainData.status;
export const selectIndicators = (state: RootState) => state.mainData.indicators;
export const selectIndicatorsLastUpdated = (state: RootState) => state.mainData.indicatorsLastUpdated;
export const selectTimeframe = (state: RootState) => state.mainData.timeframe;
export const selectTraderType = (state: RootState) => state.mainData.traderType;
export const selectView = (state: RootState) => state.mainData.view;
export const selectSecurityView = (state: RootState) => state.mainData.securityView;
export const selectSymbol = (state: RootState) => state.mainData.symbol;
export const selectFilters = (state: RootState) => state.mainData.filters;
export const selectFiltersLastUpdated = (state: RootState) => state.mainData.filtersLastUpdated;
export const selectSortCriteria = (state: RootState) => state.mainData.sortCriteria; 
export const selectSortDirections = (state: RootState) => state.mainData.sortDirections;
export const selectSortLastUpdated = (state: RootState) => state.mainData.sortLastUpdated;

export default mainDataReducer.reducer;